import { useEffect } from 'react';

export interface SEOData {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  schema?: Record<string, unknown>;
}

export function useSEO(data: SEOData) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = data.title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', data.description);
    if (data.keywords) setMeta('keywords', data.keywords);

    const canonical = data.canonicalUrl || window.location.href.split('?')[0];
    let linkEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!linkEl) {
      linkEl = document.createElement('link');
      linkEl.rel = 'canonical';
      document.head.appendChild(linkEl);
    }
    linkEl.href = canonical;

    setMeta('og:type', data.ogType || 'website', true);
    setMeta('og:title', data.ogTitle || data.title, true);
    setMeta('og:description', data.ogDescription || data.description, true);
    setMeta('og:url', canonical, true);
    if (data.ogImage) setMeta('og:image', data.ogImage, true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', data.ogTitle || data.title);
    setMeta('twitter:description', data.ogDescription || data.description);

    let schemaScript: HTMLScriptElement | null = null;
    if (data.schema) {
      schemaScript = document.createElement('script');
      schemaScript.type = 'application/ld+json';
      schemaScript.textContent = JSON.stringify(data.schema);
      document.head.appendChild(schemaScript);
    }

    return () => {
      document.title = prevTitle;
      if (schemaScript && schemaScript.parentNode) {
        schemaScript.parentNode.removeChild(schemaScript);
      }
    };
  }, [data]);
}